import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Clock, Bell, BellOff, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { subscribeToPush, unsubscribeFromPush, isPushSupported, getNotificationPermission } from '../services/pushService';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const WorkingHoursSettings = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    start_time: '11:00',
    end_time: '19:00',
    diamond_cutoff_time: '17:45',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Push notification state
  const [pushPermission, setPushPermission] = useState('unsupported');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushInServer, setPushInServer] = useState(null); // null=checking, true/false
  const [pushLoading, setPushLoading] = useState(false);
  const [testResult, setTestResult] = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    if (!isPushSupported()) return;
    const perm = getNotificationPermission();
    setPushPermission(perm);
    if (perm === 'granted') {
      subscribeToPush().then((ok) => {
        setPushSubscribed(ok);
        if (ok) {
          // Verify subscription is actually saved in server
          api.get('/push/status')
            .then(r => setPushInServer(r.data.subscription_count > 0))
            .catch(() => setPushInServer(null));
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/working-hours');
        setConfig(res.data);
      } catch (error) {
        console.error('Error fetching config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleEnablePush = async () => {
    setPushLoading(true);
    const perm = await Notification.requestPermission();
    setPushPermission(perm);
    if (perm === 'granted') {
      const ok = await subscribeToPush();
      setPushSubscribed(ok);
    }
    setPushLoading(false);
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    await unsubscribeFromPush();
    setPushPermission('default');
    setPushSubscribed(false);
    setPushInServer(false);
    setPushLoading(false);
  };

  const handleResubscribe = async () => {
    setPushLoading(true);
    setTestResult(null);
    try {
      // Force fresh subscription by unsubscribing first
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();
      // Now re-subscribe
      const ok = await subscribeToPush();
      setPushSubscribed(ok);
      if (ok) {
        const r = await api.get('/push/status');
        setPushInServer(r.data.subscription_count > 0);
        setTestResult('resubscribed');
      } else {
        setTestResult('Re-subscribe failed. Try reloading the page.');
      }
    } catch (e) {
      setTestResult('Error: ' + (e.message || 'Unknown'));
    }
    setPushLoading(false);
    setTimeout(() => setTestResult(null), 6000);
  };

  const handleTestNotification = async () => {
    setPushLoading(true);
    setTestResult(null);
    try {
      await api.post('/push/test');
      setTestResult('success');
    } catch (e) {
      setTestResult(e.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setPushLoading(false);
      setTimeout(() => setTestResult(null), 5000);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/working-hours', config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    setConfig(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day],
    }));
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <Clock className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="working-hours-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">Working Hours Settings</h1>
            <p className="text-xs text-emerald-200">Configure factory working hours</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Time Settings */}
          <div className="bg-white rounded-lg border p-4 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b pb-2">Time Configuration</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="start_time">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={config.start_time}
                  onChange={(e) => setConfig(prev => ({ ...prev, start_time: e.target.value }))}
                  data-testid="start-time-input"
                />
                <p className="text-xs text-gray-400">Factory opening</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={config.end_time}
                  onChange={(e) => setConfig(prev => ({ ...prev, end_time: e.target.value }))}
                  data-testid="end-time-input"
                />
                <p className="text-xs text-gray-400">Factory closing</p>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="cutoff_time">Diamond Parcel Cut-off Time</Label>
              <Input
                id="cutoff_time"
                type="time"
                value={config.diamond_cutoff_time}
                onChange={(e) => setConfig(prev => ({ ...prev, diamond_cutoff_time: e.target.value }))}
                className="max-w-[200px]"
                data-testid="cutoff-time-input"
              />
              <p className="text-xs text-gray-400">No new diamond parcels accepted after this time</p>
            </div>
          </div>

          {/* Working Days */}
          <div className="bg-white rounded-lg border p-4 space-y-3">
            <h2 className="text-sm font-semibold text-gray-700 border-b pb-2">Working Days</h2>
            <div className="flex flex-wrap gap-2" data-testid="working-days-selector">
              {DAYS_OF_WEEK.map(day => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    config.working_days.includes(day)
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  data-testid={`day-toggle-${day.toLowerCase()}`}
                >
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <p className="font-medium mb-1">How working hours affect the queue:</p>
            <ul className="list-disc ml-4 space-y-1 text-xs">
              <li>Priority does NOT extend working hours or override closing time</li>
              <li>Critical tasks arriving after cut-off roll to next working day at start time</li>
              <li>No artificial time bending - the system respects factory schedule</li>
            </ul>
          </div>

          {/* Push Notifications Section */}
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-sm text-gray-800">Push Notifications</h3>
            </div>

            {!isPushSupported() ? (
              <p className="text-xs text-gray-500">Push notifications are not supported in this browser.</p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Status: <span className={`font-semibold ${pushSubscribed ? 'text-emerald-600' : pushPermission === 'denied' ? 'text-red-600' : 'text-amber-600'}`}>
                        {pushSubscribed ? 'Enabled' : pushPermission === 'denied' ? 'Blocked by browser' : pushPermission === 'granted' ? 'Permission granted, re-subscribing…' : 'Not enabled'}
                      </span>
                    </p>
                    {pushSubscribed && pushInServer === false && (
                      <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                        <AlertCircle className="w-3 h-3" /> Not registered on server — tap Re-register
                      </p>
                    )}
                    {pushSubscribed && pushInServer === true && (
                      <p className="text-xs text-emerald-500 mt-0.5">Registered on server</p>
                    )}
                    {!pushSubscribed && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {pushPermission === 'denied' ? 'Reset permissions in browser settings to re-enable.' : 'Receive alerts for follow-ups, sessions & stock.'}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 items-end">
                    {pushSubscribed ? (
                      <Button size="sm" variant="outline" onClick={handleDisablePush} disabled={pushLoading} className="text-red-600 border-red-300 hover:bg-red-50 h-8 text-xs" data-testid="disable-push-settings-btn">
                        <BellOff className="w-3.5 h-3.5 mr-1" /> Disable
                      </Button>
                    ) : pushPermission !== 'denied' ? (
                      <Button size="sm" onClick={handleEnablePush} disabled={pushLoading} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="enable-push-settings-btn">
                        <Bell className="w-3.5 h-3.5 mr-1" /> Enable
                      </Button>
                    ) : null}
                    {pushSubscribed && (
                      <Button size="sm" variant="ghost" onClick={handleResubscribe} disabled={pushLoading} className="h-7 text-xs text-gray-500 hover:text-gray-700" data-testid="resubscribe-push-btn">
                        <RefreshCw className="w-3 h-3 mr-1" /> Re-register
                      </Button>
                    )}
                  </div>
                </div>

                {pushPermission === 'granted' && (
                  <div className="pt-1 border-t border-gray-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleTestNotification}
                      disabled={pushLoading}
                      className="w-full h-9 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="test-push-notification-btn"
                    >
                      {pushLoading ? 'Sending…' : <><Bell className="w-3.5 h-3.5 mr-1.5" /> Send Test Notification</>}
                    </Button>
                    {testResult === 'success' && (
                      <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2" data-testid="push-test-success">
                        <CheckCircle className="w-3.5 h-3.5" /> Test notification sent! Check your device.
                      </p>
                    )}
                    {testResult === 'resubscribed' && (
                      <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2">
                        <CheckCircle className="w-3.5 h-3.5" /> Re-registered! Tap "Send Test Notification" to verify.
                      </p>
                    )}
                    {testResult && testResult !== 'success' && testResult !== 'resubscribed' && (
                      <p className="text-xs text-red-500 mt-2" data-testid="push-test-error">{testResult}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#075E54] hover:bg-[#064E46] h-11"
            data-testid="save-working-hours-btn"
          >
            {saving ? 'Saving...' : saved ? 'Saved!' : (
              <><Save className="w-4 h-4 mr-2" /> Save Working Hours</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursSettings;
